import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Confirm from 'components/Confirm';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { bem } from 'helpers/class-name';

import messages from '../ModifierLibrary.messages';
import './DeleteModifierGroupModal.scss';

const { block, element } = bem('delete-modifier-group-modal');

export const DELETE_MODIFIER_GROUP_MODAL_NAME = 'DeleteModifierGroupModal';

const DeleteModifierGroupModal = ({ groupName, itemNames, onConfirmDelete }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const handleCancel = () => {
    if (!isDeleting) {
      dispatch(closeModal());
    }
  };

  const handleConfirm = async () => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);
    setHasFailed(false);
    try {
      await onConfirmDelete?.();
      dispatch(closeModal());
    } catch (error) {
      setHasFailed(true);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={block()}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage(messages.deleteGroupDialogLabel)}>
      <Confirm
        className={element('content')}
        title={
          <Message
            message={messages.deleteGroupTitle}
            values={{ groupName, strong: chunks => <strong>{chunks}</strong> }}
          />
        }
        cancelNode={<Message message={messages.deleteGroupCancel} />}
        acceptNode={<Message message={messages.deleteGroupAction} />}
        acceptDisabled={isDeleting}
        acceptWaiting={isDeleting}
        onCancel={handleCancel}
        onRequestClose={handleCancel}
        onConfirm={handleConfirm}>
        {hasFailed && (
          <p className={element('error')}>
            <Message message={messages.deleteGroupFailed} />
          </p>
        )}
        {itemNames.length > 0 ? (
          <>
            <p className={element('line')}>
              <Message
                message={messages.deleteGroupUsedIn}
                values={{
                  itemCount: itemNames.length,
                  itemNames: intl.formatList(itemNames, { type: 'conjunction' }),
                }}
              />
            </p>
            <p className={element('line')}>
              <Message message={messages.deleteGroupUndone} />
            </p>
          </>
        ) : (
          <Message message={messages.deleteGroupUnused} />
        )}
      </Confirm>
    </div>
  );
};

DeleteModifierGroupModal.propTypes = {
  groupName: PropTypes.string,
  itemNames: PropTypes.arrayOf(PropTypes.string),
  onConfirmDelete: PropTypes.func,
};

DeleteModifierGroupModal.defaultProps = {
  groupName: '',
  itemNames: [],
};

export default DeleteModifierGroupModal;
